// src/pages/blogs.js

import React, { useMemo } from 'react';
import { graphql, Link, navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout } from '@components';
import BlogList from '@components/bloglist';
import TagFilter from '@components/tagfilter';

// ──────────────── STYLES ────────────────
/* No width or padding of its own: the global main rule already supplies the
   page gutters, exactly as it does on the home page. The column inside is the
   same 700px the #blogs section uses, centred the same way, so the two
   surfaces line up to the pixel. */
const Section = styled.main`
  .inner {
    max-width: 700px;
    margin: 0 auto;
    margin-top: -1rem;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    margin: var(--space-6) 0 var(--space-5);
  }

  /* Keeps Clear tied to the trigger it undoes, so space-between still pushes
     the count to the far edge rather than stranding Clear in the middle. */
  .filter-group {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .clear {
    /* Same optical correction as the count: a blockified flex item is centred
       by its line box, which leaves the cap band riding high. */
    position: relative;
    top: var(--optical);
    padding: 0;
    background-color: transparent;
    border: 0;
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-md);
    line-height: 1;
    cursor: pointer;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: var(--accent);
      outline: none;
    }
  }

  /* The trigger next to it carries the site's asymmetric optical padding, so
     its glyphs sit on its border-box centre. A plain span does not — flexbox
     centres its line box, which leaves the cap band riding 0.17em high. */
  .result-count {
    position: relative;
    top: var(--optical);
    color: var(--text-muted);
    font-size: var(--fz-md);
  }

  .empty {
    padding: var(--space-8) 0;
    color: var(--text-muted);
    text-align: center;
  }
`;

const BlogsPage = ({ data, location }) => {
  const blogs = data.blogsRemark.edges.map(edge => edge.node.frontmatter);

  const selected = new URLSearchParams(location.search || '').get('tag');

  const { tags, counts } = useMemo(() => {
    const byTag = {};
    blogs.forEach(a => (a.tags || []).forEach(t => (byTag[t] = (byTag[t] || 0) + 1)));
    return {
      tags: Object.keys(byTag).sort((a, b) => byTag[b] - byTag[a] || a.localeCompare(b)),
      counts: { total: blogs.length, byTag },
    };
  }, [blogs]);

  const visible = selected ? blogs.filter(a => (a.tags || []).includes(selected)) : blogs;

  const onSelect = tag => {
    const url = tag ? `${location.pathname}?tag=${encodeURIComponent(tag)}` : location.pathname;
    navigate(url, { replace: true });
  };

  return (
    <Layout location={location}>
      <Helmet title="Blogs" />
      <Section>
        <div className="inner">
          <span className="breadcrumb">
            <Link to="/">
              <span className="arrow">&larr; Back</span>
            </Link>
          </span>
          <h1 className="small-heading">Blogs</h1>
          <p className="subtitle">
            Notes on AI, philosophy, and whatever else I’ve been thinking about — some finished,
            some still in progress.
          </p>

          <div className="controls">
            <div className="filter-group">
              <TagFilter tags={tags} counts={counts} selected={selected} onSelect={onSelect} />
              {selected && (
                <button type="button" className="clear" onClick={() => onSelect(null)}>
                  Clear
                </button>
              )}
            </div>
            <span className="result-count">
              {visible.length} {visible.length === 1 ? 'blog' : 'blogs'}
            </span>
          </div>

          {visible.length > 0 ? (
            <BlogList blogs={visible} />
          ) : (
            <p className="empty">Nothing here yet under that topic.</p>
          )}
        </div>
      </Section>
    </Layout>
  );
};

BlogsPage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.shape({
    blogsRemark: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({ node: PropTypes.shape({ frontmatter: PropTypes.object }) }),
      ),
    }),
  }).isRequired,
};

export default BlogsPage;

export const pageQuery = graphql`
  {
    blogsRemark: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/content/blogs/" } }
      sort: { fields: [frontmatter___date], order: DESC }
      limit: 1000
    ) {
      edges {
        node {
          frontmatter {
            title
            slug
            date
            tags
            description
          }
        }
      }
    }
  }
`;
